import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useEvaluationList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refEvaluationTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'evaluation.period',
      label: 'Period',
      sortable: true,
    },
    {
      key: 'purchaseOrder',
      label: 'latest purchase order',
      sortable: false,
    },
    {
      key: 'ranking',
      label: 'ranking',
      sortable: false,
    },
    {
      key: 'remarks',
      label: 'justification / remarks',
      sortable: false,
    },
    {
      key: 'units',
      label: 'Unit',
      sortable: false,
    },
    {
      key: 'unitCosts',
      label: 'Unit Price (S$)',
      sortable: false,
    },
    {
      key: 'unitCosts',
      label: 'Unit Price (S$)',
      sortable: false,
    },
    {
      key: 'adjustedUnitPrices',
      label: 'Adj Unit price',
      sortable: false,
    },
    {
      key: 'creditTermDays',
      label: 'Credit term (days)',
      sortable: false,
    },
    {
      key: 'lateDeliveryDays',
      label: 'Late delivery (days)',
      sortable: false,
    },
    {
      key: 'lateDeliveryTimes',
      label: 'Late delivery (times)',
      sortable: false,
    },
    {
      key: 'numberOfNCF',
      label: 'No. of NCF',
      sortable: false,
    },
    {
      key: 'numberOfAdjustedUnitPrice',
      label: 'adj unit price',
      sortable: false,
    },
    {
      key: 'numberOfCreditTerm',
      label: 'Credit term',
      sortable: false,
    },
    {
      key: 'numberOfDelivery',
      label: 'Delivery/service',
      sortable: false,
    },
    {
      key: 'quantity',
      label: 'quality',
      sortable: false,
    },
    {
      key: 'totalPoint',
      label: 'total points',
      sortable: false,
    },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalEvaluation = ref(0)
  const searchQuery = ref(route.query.search || '')
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const sortBy = ref(route.query.sortBy ?? 'createdAt')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const toDeletedID = ref(null)
  const evaluationData = ref([])
  const currentDataLength = ref(0)
  const entity = ref([])
  if (Array.isArray(route.query.entity)) {
    entity.value = route.query.entity
  } else if (route.query.entity) {
    entity.value = [route.query.entity]
  }
  const periodFilter = ref(route.query.period || '')
  const vendorFilter = ref(route.params.id || null)
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refEvaluationTable.value ? refEvaluationTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalEvaluation.value,
    }
  })

  const refetchData = () => {
    refEvaluationTable.value.refresh()
  }

  watch([searchQuery, currentPage, entity, periodFilter], () => {
    refetchData()
  }, { deep: true })

  const fetchEvaluation = (ctx, callback) => {
    store
      .dispatch('app-vendor-evaluations/fetchEvaluations', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        entity: entity.value,
        period: periodFilter.value,
        vendorFilter: vendorFilter.value,
      },
      { root: true })
      .then(response => {
        const { evaluations, total } = response.data
        evaluationData.value = evaluations
        currentDataLength.value = evaluations.length
        callback(evaluations)
        totalEvaluation.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              entity: entity.value,
              period: periodFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteEvaluation = () => {
    store
      .dispatch('app-vendor-evaluations/deleteEvaluation', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        console.log(error)
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*
  const resolveEvaluationStatusVariant = status => {
    if (status === 'approved') return 'success'
    if (status === 'pending signatories') return 'warning'
    if (status === 'draft') return 'secondary'
    return 'primary'
  }

  return {
    fetchEvaluation,
    tableColumns,
    perPage,
    currentPage,
    evaluationData,
    totalEvaluation,
    dataMeta,
    perPageOptions,
    sortBy,
    isSortDirDesc,
    refEvaluationTable,

    searchQuery,

    refetchData,
    currentDataLength,

    toDeletedID,
    deleteEvaluation,

    entity,
    periodFilter,
    resolveEvaluationStatusVariant,
  }
}
